/** Column heatmaps — per-column min/max, scale chosen by semantic type. */

export type HeatmapScale = 'strength' | 'magnitude' | 'sequential'

type Rgb = { r: number; g: number; b: number }

const COLOR_WHITE: Rgb = { r: 255, g: 255, b: 255 }

/** Strength: lower = worse, higher = better */
const COLOR_LOW_STRENGTH: Rgb = { r: 185, g: 28, b: 28 } // red-700
const COLOR_HIGH_STRENGTH: Rgb = { r: 21, g: 128, b: 61 } // green-700

/** Magnitude (not moral): low = blue, high = orange */
const COLOR_LOW_MAGNITUDE: Rgb = { r: 29, g: 78, b: 216 } // blue-700
const COLOR_HIGH_MAGNITUDE: Rgb = { r: 234, g: 88, b: 12 } // orange-500

/** Sequential: likely outcomes pop (green intensity), low values stay white */
const COLOR_SEQUENTIAL_HIGH: Rgb = { r: 21, g: 128, b: 61 } // green-700

const ALPHA_MIN = 0.2
const ALPHA_MAX = 0.42
/** Within this distance of column midpoint (diverging), cell stays untinted. */
const NEUTRAL_BAND = 0.08

export type ColumnRange = { min: number; max: number }

export function coerceNumeric(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed || trimmed === '—') return null
    const n = Number(trimmed)
    return Number.isFinite(n) ? n : null
  }
  return null
}

export function computeColumnRanges<T>(
  rows: readonly T[],
  columnKeys: readonly string[],
  getValue: (row: T, columnKey: string) => unknown,
): Record<string, ColumnRange> {
  const out: Record<string, ColumnRange> = {}
  for (const key of columnKeys) {
    let min = Infinity
    let max = -Infinity
    for (const row of rows) {
      const n = coerceNumeric(getValue(row, key))
      if (n == null) continue
      if (n < min) min = n
      if (n > max) max = n
    }
    if (min !== Infinity && max !== -Infinity) {
      out[key] = { min, max }
    }
  }
  return out
}

function mixChannel(a: number, b: number, t: number): number {
  return Math.round(a + (b - a) * t)
}

function mixRgba(from: Rgb, to: Rgb, t: number, alpha: number): string {
  const clamped = Math.max(0, Math.min(1, t))
  const r = mixChannel(from.r, to.r, clamped)
  const g = mixChannel(from.g, to.g, clamped)
  const b = mixChannel(from.b, to.b, clamped)
  return `rgba(${r}, ${g}, ${b}, ${alpha.toFixed(3)})`
}

/** Map value into [0,1] within range; higher → closer to 1. */
export function normalizedHeatmapT(value: number, range: ColumnRange): number {
  if (range.max === range.min) return 0.5
  return (value - range.min) / (range.max - range.min)
}

function alphaForDivergingArm(armT: number): number {
  return ALPHA_MIN + (ALPHA_MAX - ALPHA_MIN) * armT
}

/** Red/blue ↔ white ↔ green/orange; white (no fill) near the midpoint. */
function divergingBackground(t: number, colorLow: Rgb, colorHigh: Rgb): string | undefined {
  const clamped = Math.max(0, Math.min(1, t))
  if (clamped <= 0.5) {
    const arm = clamped * 2
    if (1 - arm < NEUTRAL_BAND) return undefined
    const alpha = alphaForDivergingArm(1 - arm)
    return mixRgba(colorLow, COLOR_WHITE, arm, alpha)
  }
  const arm = (clamped - 0.5) * 2
  if (arm < NEUTRAL_BAND) return undefined
  const alpha = alphaForDivergingArm(arm)
  return mixRgba(COLOR_WHITE, colorHigh, arm, alpha)
}

/** White at low values; stronger green toward column max (World Cup–style emphasis). */
function sequentialBackground(t: number): string | undefined {
  const clamped = Math.max(0, Math.min(1, t))
  if (clamped < 0.12) return undefined
  const alpha = ALPHA_MIN + ((ALPHA_MAX - ALPHA_MIN) * (clamped - 0.12)) / 0.88
  return mixRgba(COLOR_WHITE, COLOR_SEQUENTIAL_HIGH, clamped, alpha)
}

export function heatmapBackground(t: number, scale: HeatmapScale): string | undefined {
  switch (scale) {
    case 'strength':
      return divergingBackground(t, COLOR_LOW_STRENGTH, COLOR_HIGH_STRENGTH)
    case 'magnitude':
      return divergingBackground(t, COLOR_LOW_MAGNITUDE, COLOR_HIGH_MAGNITUDE)
    case 'sequential':
      return sequentialBackground(t)
    default:
      return divergingBackground(t, COLOR_LOW_STRENGTH, COLOR_HIGH_STRENGTH)
  }
}

type CSSProperties = Record<string, string | number | undefined>

export function cellHeatmapStylePlain(
  value: unknown,
  range: ColumnRange | undefined,
  enabled: boolean,
  scale: HeatmapScale = 'strength',
): CSSProperties | undefined {
  if (!enabled || !range) return undefined
  const n = coerceNumeric(value)
  if (n == null) return undefined
  const t = normalizedHeatmapT(n, range)
  const bg = heatmapBackground(t, scale)
  return bg ? { backgroundColor: bg } : undefined
}
